import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Target, Eye, Compass, Users, Heart, Shield, Sparkles, TrendingUp } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";

const values = [
  { icon: Shield, title: "Integrity", desc: "We do what we say. Commitments earn their weight in execution, not in pitch decks." },
  { icon: Sparkles, title: "Craft", desc: "Telecom systems are unforgiving — we obsess over the details that keep networks running." },
  { icon: Users, title: "Partnership", desc: "We embed with your teams. Knowledge transfer is a feature, not a clause." },
  { icon: TrendingUp, title: "Outcomes", desc: "Roadmaps matter only when they ship. Every engagement ties back to measurable results." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-48 pb-16 md:pb-20 text-center">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative max-w-4xl mx-auto px-6">
          <span className="section-label">Our Story</span>
          <h1 className="mt-6 font-semibold tracking-tight leading-[1.08] text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl">
            About <span className="gradient-text">Moxizo</span>
          </h1>
          <p className="mt-6 md:mt-7 text-sm sm:text-base md:text-lg text-gray-400 max-w-[34ch] sm:max-w-xl md:max-w-2xl mx-auto leading-relaxed">
            A telecom consulting firm built on two decades of operator experience — BSS, testing, managed services and strategy for the teams running global networks.
          </p>
        </div>
      </section>

      {/* What is Moxizo */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <SectionHeader
            label="Introduction"
            title="What is"
            gradientWord="Moxizo?"
            subtitle="Moxizo is a specialised telecom consulting firm that delivers high-stakes technology services to operators worldwide. We are not a generalist agency — we are telecom-native specialists who have spent decades inside the world's largest networks, shipping code and running carriers."
          />
          
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { 
                icon: Target, 
                title: "Our Mission", 
                subtitle: "Make telecom operations predictable, scalable and revenue-safe.", 
                desc: "We help operators modernise the BSS core, enforce quality through end-to-end testing, and run the stack with senior engineers, not contractors. The goal: zero surprise at launch, measurable uplift after." 
              },
              { 
                icon: Eye, 
                title: "Our Vision", 
                subtitle: "A world where launching a new telecom service feels effortless.", 
                desc: "We believe operators should compete on customer experience — not on whether the billing system can handle a new price plan. Our work is to make the back-office invisible, so the front-office can move." 
              }
            ].map((item, i) => (
              <div key={i} className="card-base p-8 md:p-12 text-left hover:border-cyan-400/30 transition-all group relative overflow-hidden h-full">
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400/5 blur-3xl rounded-full translate-x-16 -translate-y-16 group-hover:bg-cyan-400/10 transition-all" />
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="icon-circle w-12 h-12"><item.icon className="w-5 h-5 text-cyan-400" /></div>
                    <span className="text-sm font-bold tracking-widest uppercase text-cyan-400/80">{item.title}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-medium leading-tight mb-5 group-hover:text-white transition-colors">{item.subtitle}</h3>
                  <p className="text-gray-400 leading-relaxed text-base md:text-lg">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="Our Story"
            title="The"
            gradientWord="Journey"
            subtitle="Twenty years of building and operating the complex digital infrastructure behind global telecom operators. We bridge the gap between high-level strategic vision and the technical reality of live networks."
          />

          <div className="grid md:grid-cols-2 gap-14 items-center">
            <div className="flex flex-col justify-center py-4 order-2 md:order-1">
              <div className="space-y-6 text-lg text-gray-300 leading-relaxed mb-4">
                <p>
                  Moxizo was founded to close the gap between boardroom strategy and the engineers operating real telecom systems. We've delivered BSS transformations, migrated legacy billing platforms, and stood up testing programs that caught revenue leakage before launch.
                </p>
                <p>
                  Our team has worked inside Tier-1 operators, watched regulations shift and seen how quickly customer expectations outpace legacy stacks. That operator-first perspective shapes every engagement we take on.
                </p>
                <p>
                  Today we partner with operators across India, the Middle East and Africa — blending managed services, testing, PMO governance and strategic consulting under one roof.
                </p>
              </div>
              <div className="mt-10">
                <Link to="/services" className="btn-primary inline-flex">
                  Explore Our Services <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="relative order-1 md:order-2">
              <div className="absolute -inset-6 bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-600/10 blur-2xl" />
              <div className="relative card-base overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/12712432/pexels-photo-12712432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Telecom tower"
                  className="w-full h-[520px] object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/20 border border-cyan-400/30 flex items-center justify-center">
                    <Compass className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Operator-first perspective</div>
                    <div className="text-xs text-gray-400">Consultants who shipped into live networks</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            label="Core Values"
            title="Our"
            gradientWord="Values"
            subtitle="The principles that shape how we engage, deliver and grow long-term relationships with the operators we work alongside."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div key={v.title} className="card-base p-7" data-testid={`value-${i}`}>
                <div className="icon-circle mb-6"><v.icon className="w-5 h-5" /></div>
                <h3 className="text-lg font-medium">{v.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative card-base overflow-hidden p-12 md:p-16 text-center">
            <div className="absolute inset-0 grid-pattern opacity-40" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                Ready to transform your <span className="gradient-text">telecom operations?</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg mb-10 leading-relaxed">
                Whether you're planning a BSS migration or need senior managed support — we're ready when you are.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="btn-primary">
                  Start a Conversation <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/" className="btn-ghost">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
